import React, { useState, useMemo } from 'react';
import { useI18n } from '../i18n';
import { Shield, Lock, Cpu, ArrowRight, CheckCircle, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { trackDlpPresetSelect, trackDlpVaultInspect } from '../lib/analytics';

interface DlpTokenMatch {
  tag: string;
  original: string;
  type: string;
}

export const DlpDemo: React.FC = () => {
  const { t, locale } = useI18n();

  const presetsByLocale: Record<
    string,
    { card: string; apiSecret: string; pii: string }
  > = {
    en: {
      card: 'Process refund of $142.50 for customer Sarah Connor using Visa card 4532-8921-3849-1092 and email sarah.c@cyberdyne.io.',
      apiSecret:
        'Deploy the payment gateway with AWS_KEY=AKIAIOSFODNN7EXAMPLE and secret sk-live-94827103984710928374 on cluster-us-east.',
      pii: 'Review background check for employee James Wilson (SSN: 923-45-6789) born in 1988, contact at jwilson@enterprise.com.',
    },
    tr: {
      card: 'Müşteri Mehmet Yılmaz için 4532-8921-3849-1092 nolu Visa kartına 4.850 TL iade işlemini mehmet.yilmaz@sirket.com.tr adresine bildirin.',
      apiSecret:
        'Üretim ortamı ödeme servisini AWS_KEY=AKIAIOSFODNN7EXAMPLE ve sk-live-94827103984710928374 anahtarlarıyla prod-ist kümesine dağıtın.',
      pii: 'Yeni çalışan Ayşe Demir için 12345678901 T.C. Kimlik Numarası ile SGK giriş kaydını ayse.demir@kurum.com.tr üzerinden onaylayın.',
    },
    es: {
      card: 'Procesar reembolso de €180.00 para Carlos Rivera con tarjeta Visa 4532-8921-3849-1092 y correo carlos.rivera@empresa.es.',
      apiSecret:
        'Desplegar la pasarela de pagos con credenciales AWS_KEY=AKIAIOSFODNN7EXAMPLE y token sk-live-94827103984710928374 en el clúster Madrid.',
      pii: 'Verificar expediente del colaborador Alejandro Gómez (DNI: 48392019K) y enviar confirmación a alejandro.gomez@corporacion.es.',
    },
  };

  const activePresets = presetsByLocale[locale] || presetsByLocale.en;
  const [inputText, setInputText] = useState(activePresets.card);
  const [showTokensOnly, setShowTokensOnly] = useState(false);

  React.useEffect(() => {
    setInputText(activePresets.card);
  }, [locale]);

  // Real-time DLP Sanitization Simulation Engine
  const { sanitizedText, tokenVault, redactedCount } = useMemo(() => {
    const vault: DlpTokenMatch[] = [];
    let counter = 1;
    let text = inputText;

    // 1. Credit Card Pattern
    const cardRegex = /\b(?:\d{4}[ -]?){3}\d{4}\b/g;
    text = text.replace(cardRegex, (match) => {
      const tag = `{{CREDIT_CARD_${counter++}}}`;
      vault.push({ tag, original: match, type: 'Credit Card (PCI-DSS)' });
      return tag;
    });

    // 2. API Keys & Secrets Pattern
    const secretRegex = /\b(AKIA[0-9A-Z]{16}|sk-[a-zA-Z0-9_\-]{20,}|ghp_[a-zA-Z0-9]{20,})\b/g;
    text = text.replace(secretRegex, (match) => {
      const tag = `{{API_SECRET_${counter++}}}`;
      vault.push({ tag, original: match, type: 'API Key / Secret' });
      return tag;
    });

    // 3. National ID (SSN / TCKN / DNI)
    const idRegex = /\b(\d{3}-\d{2}-\d{4}|\d{11}|\d{8}[A-Z])\b/g;
    text = text.replace(idRegex, (match) => {
      const tag = `{{NATIONAL_ID_${counter++}}}`;
      vault.push({ tag, original: match, type: 'National ID / SSN / TCKN' });
      return tag;
    });

    // 4. Email addresses
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
    text = text.replace(emailRegex, (match) => {
      const tag = `{{EMAIL_ADDRESS_${counter++}}}`;
      vault.push({ tag, original: match, type: 'Customer Email PII' });
      return tag;
    });

    return {
      sanitizedText: text,
      tokenVault: vault,
      redactedCount: vault.length,
    };
  }, [inputText]);

  // Simulated LLM response with stream reconstruction
  const simulatedLlmResponse = useMemo(() => {
    if (tokenVault.length === 0) {
      return locale === 'tr'
        ? 'İstek başarıyla analiz edildi. Herhangi bir hassas PII verisi tespit edilmedi.'
        : locale === 'es'
        ? 'Solicitud analizada correctamente. No se detectaron datos PII sensibles.'
        : 'Request analyzed successfully. No sensitive credentials or PII detected.';
    }

    if (locale === 'tr') {
      return `Onaylandı. İşlem DLP Koruması altında güvenle gerçekleştirildi. Hedef hesap (${tokenVault[0]?.original}) için işlem referansı #WK-893021 üretildi.`;
    }
    if (locale === 'es') {
      return `Confirmado. La operación se ejecutó de forma segura bajo protección DLP. Se ha generado la referencia #WK-893021 para (${tokenVault[0]?.original}).`;
    }
    return `Confirmed. The transaction was executed securely under DLP protection. Reference #WK-893021 generated for (${tokenVault[0]?.original}).`;
  }, [tokenVault, locale]);

  return (
    <section id="dlp" className="py-20 md:py-28 relative bg-slate-100/70 dark:bg-navy-950/60 border-y border-slate-200 dark:border-navy-800/80 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold-500/10 border border-gold-500/30 text-xs font-semibold text-gold-500 dark:text-gold-400 mb-4 uppercase tracking-wider">
            <Shield className="w-3.5 h-3.5" />
            <span>{t.dlpDemo.badge}</span>
          </div>
          <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-4">
            {t.dlpDemo.title}
          </h2>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300">
            {t.dlpDemo.subtitle}
          </p>
        </div>

        {/* Preset Selector */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-8">
          <span className="text-xs font-medium text-slate-500 dark:text-slate-400 mr-2 flex items-center gap-1.5">
            <RefreshCw className="w-3.5 h-3.5 text-gold-500" />
            {t.dlpDemo.samplePromptLabel}
          </span>
          <button
            onClick={() => {
              setInputText(activePresets.card);
              trackDlpPresetSelect('card', locale);
            }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              inputText === activePresets.card
                ? 'bg-gold-500 text-navy-950 font-semibold shadow-gold-glow'
                : 'bg-white dark:bg-navy-850 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-navy-800 border border-slate-300 dark:border-navy-700'
            }`}
          >
            {t.dlpDemo.presets.card}
          </button>
          <button
            onClick={() => {
              setInputText(activePresets.apiSecret);
              trackDlpPresetSelect('apiSecret', locale);
            }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              inputText === activePresets.apiSecret
                ? 'bg-gold-500 text-navy-950 font-semibold shadow-gold-glow'
                : 'bg-white dark:bg-navy-850 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-navy-800 border border-slate-300 dark:border-navy-700'
            }`}
          >
            {t.dlpDemo.presets.apiSecret}
          </button>
          <button
            onClick={() => {
              setInputText(activePresets.pii);
              trackDlpPresetSelect('pii', locale);
            }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              inputText === activePresets.pii
                ? 'bg-gold-500 text-navy-950 font-semibold shadow-gold-glow'
                : 'bg-white dark:bg-navy-850 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-navy-800 border border-slate-300 dark:border-navy-700'
            }`}
          >
            {t.dlpDemo.presets.pii}
          </button>
        </div>

        {/* Interactive Live DLP Sandbox Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          {/* Panel 1: Raw User Input */}
          <div className="glow-card rounded-2xl p-5 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-200 dark:border-navy-700/80">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-slate-400" />
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wide">
                    {t.dlpDemo.rawInputLabel}
                  </span>
                </div>
                <span className="text-[10px] font-mono text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded border border-emerald-200 dark:border-emerald-800/60 font-semibold">
                  Internal Perimeter
                </span>
              </div>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={t.dlpDemo.rawInputPlaceholder}
                rows={5}
                className="w-full bg-slate-50 dark:bg-navy-950/80 text-slate-800 dark:text-slate-200 text-xs sm:text-sm font-mono p-3 rounded-lg border border-slate-300 dark:border-navy-700 focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500/30 transition-all resize-none"
              />
            </div>

            <div className="mt-4 pt-3 border-t border-slate-200 dark:border-navy-800 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
              <span>{inputText.length} characters</span>
              <span className="text-gold-600 dark:text-gold-400 font-mono font-medium">Status: Plaintext in RAM</span>
            </div>
          </div>

          {/* Panel 2: Sanitized Prompt (Transmitted to LLM) */}
          <div className="glow-card rounded-2xl p-5 flex flex-col justify-between border-gold-500/40 shadow-gold-glow">
            <div>
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-200 dark:border-navy-700/80">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-gold-500" />
                  <span className="text-xs font-bold text-gold-600 dark:text-gold-400 uppercase tracking-wide">
                    {t.dlpDemo.sanitizedLabel}
                  </span>
                </div>
                <span className="text-[10px] font-mono text-gold-600 dark:text-gold-400 bg-gold-500/10 px-2 py-0.5 rounded border border-gold-500/40 font-semibold">
                  {redactedCount} {t.dlpDemo.redactedBadge}
                </span>
              </div>

              <div className="bg-slate-50 dark:bg-navy-950/90 p-3 rounded-lg border border-slate-200 dark:border-navy-800 min-h-[120px] font-mono text-xs sm:text-sm text-slate-800 dark:text-slate-300 leading-relaxed break-words">
                {sanitizedText.split(/(\{\{[A-Z0-9_]+\}\})/).map((part, index) => {
                  if (part.startsWith('{{') && part.endsWith('}}')) {
                    return (
                      <span
                        key={index}
                        className="inline-block bg-gold-500 text-navy-950 font-bold px-1.5 py-0.5 rounded mx-0.5 shadow-sm text-xs"
                      >
                        {part}
                      </span>
                    );
                  }
                  return <span key={index}>{part}</span>;
                })}
              </div>

              <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400 italic">
                {t.dlpDemo.sanitizedSubtext}
              </p>
            </div>

            {/* Token Mapping Vault Drawer */}
            <div className="mt-4 pt-3 border-t border-slate-200 dark:border-navy-800">
              <div className="flex items-center justify-between text-[11px] mb-2">
                <span className="font-semibold text-slate-700 dark:text-slate-300">Ephemeral Token Vault</span>
                <button
                  type="button"
                  onClick={() => {
                    const nextState = !showTokensOnly;
                    setShowTokensOnly(nextState);
                    trackDlpVaultInspect(nextState);
                  }}
                  className="text-gold-600 dark:text-gold-400 hover:text-gold-500 flex items-center gap-1 text-[10px] font-medium"
                >
                  {showTokensOnly ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  <span>{showTokensOnly ? 'Hide Real' : 'Inspect'}</span>
                </button>
              </div>

              {tokenVault.length > 0 ? (
                <div className="space-y-1 max-h-20 overflow-y-auto pr-1">
                  {tokenVault.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between text-[10px] bg-slate-100 dark:bg-navy-900 px-2 py-1 rounded border border-slate-200 dark:border-navy-800 font-mono"
                    >
                      <span className="text-gold-600 dark:text-gold-400 font-bold">{item.tag}</span>
                      <ArrowRight className="w-3 h-3 text-slate-400" />
                      <span className="text-slate-700 dark:text-slate-300 font-medium">
                        {showTokensOnly ? item.original : '••••••••••••'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-[10px] text-slate-400 font-mono">No sensitive tokens detected</span>
              )}
            </div>
          </div>

          {/* Panel 3: Dynamic Response Restoration */}
          <div className="glow-card rounded-2xl p-5 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-200 dark:border-navy-700/80">
                <div className="flex items-center gap-2">
                  <Cpu className="w-4 h-4 text-blue-500" />
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wide">
                    {t.dlpDemo.restoredLabel}
                  </span>
                </div>
                <span className="text-[10px] font-mono text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/60 px-2 py-0.5 rounded border border-blue-200 dark:border-blue-800/60 font-semibold">
                  Client-side Stream
                </span>
              </div>

              <div className="bg-slate-50 dark:bg-navy-950/90 p-3 rounded-lg border border-slate-200 dark:border-navy-800 min-h-[120px] font-sans text-xs sm:text-sm text-slate-800 dark:text-slate-200 leading-relaxed">
                <p>{simulatedLlmResponse}</p>
              </div>

              <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                {t.dlpDemo.restoredSubtext}
              </p>
            </div>

            <div className="mt-4 pt-3 border-t border-slate-200 dark:border-navy-800 flex items-center gap-2 text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
              <CheckCircle className="w-4 h-4 shrink-0 text-emerald-500" />
              <span>{t.dlpDemo.zeroStorageBadge}</span>
            </div>
          </div>
        </div>

        {/* Ephemeral Architecture Banner */}
        <div className="mt-8 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white dark:bg-navy-850 border border-slate-200 dark:border-navy-700 text-xs font-mono text-slate-700 dark:text-slate-300 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            {t.dlpDemo.vaultStatus}
          </span>
        </div>
      </div>
    </section>
  );
};
